// Global Instructions Rule Applied!
//
// Pipeline age helpers for the deal card counters.
//   - daysSince: whole days between a date anchor and today (never negative).
//   - resolveBandColour: maps an age to a configured warn/crit colour, or null
//     when no band applies (absence of a threshold does nothing).
// Thresholds + colours come from cfg on pipeline_config (days-in-stage) and
// pipelines (days-in-pipeline), delivered via the cached global configs.

import dayjs from 'dayjs'

/** Fallback band colours when a threshold is set but its colour is left blank. */
export const DEFAULT_WARN_COLOUR = '#EF9F27'
export const DEFAULT_CRIT_COLOUR = '#E24B4A'

/**
 * Whole days from `dateIso` to today (local). Returns null for missing/invalid
 * input; clamps negatives (future dates) to 0.
 */
export function daysSince(dateIso: string | null | undefined): number | null {
  if (!dateIso) return null
  const d = dayjs(dateIso)
  if (!d.isValid()) return null
  const diff = dayjs().startOf('day').diff(d.startOf('day'), 'day')
  return diff < 0 ? 0 : diff
}

/**
 * Resolve the colour for an age against a two-band (warn/crit) threshold set.
 * Crit wins over warn. Returns null when no band applies — the counter then
 * renders neutral. A null threshold means that band is not configured.
 */
export function resolveBandColour(
  days: number | null,
  warnDays: number | null | undefined,
  warnColour: string | null | undefined,
  critDays: number | null | undefined,
  critColour: string | null | undefined
): string | null {
  if (days == null) return null
  if (critDays != null && days >= critDays) return critColour || DEFAULT_CRIT_COLOUR
  if (warnDays != null && days >= warnDays) return warnColour || DEFAULT_WARN_COLOUR
  return null
}
